/**
 * Herstel Submission Types
 * Herstelverklaring data submitted from the portal per rapport + inspectie type
 * (installateur gegevens, signature/upload and formal submission state).
 */

import type { Database } from '../database.types'
import type { InspectieType } from '../shared/enums'

type SupabaseHerstelSubmission = Database['public']['Tables']['herstel_submissions']['Row']

/** How the installateur gegevens are delivered */
export type HerstelSubmissionMethod = 'digital' | 'manual'

/**
 * Installateur gegevens filled in on the herstelverklaring form.
 * Field names mirror the printable form (and the legacy rapporten.installateur JSONB).
 */
export interface InstallateurGegevens {
  Bedrijfsnaam: string
  NaamVerantwoordelijke: string
  Functie: string
  Certificatiestatus: string
  Adres: string
  PostcodeenPlaats: string
  Emailadres: string
  Telnr: string
}

/** JSONB/enum column overrides for herstel_submissions */
export type HerstelSubmissionOverrides = {
  inspectie_type: InspectieType
  submission_method: HerstelSubmissionMethod | null
  installateur: InstallateurGegevens
}

/** Enhanced herstel submission row with typed JSONB columns */
export interface HerstelSubmission
  extends Omit<SupabaseHerstelSubmission, keyof HerstelSubmissionOverrides>,
    HerstelSubmissionOverrides {}

/** Fields the portal may write when drafting/submitting a herstelverklaring */
export type HerstelSubmissionUpsert = Partial<
  Pick<
    HerstelSubmission,
    'submission_method' | 'installateur' | 'handtekening_url' | 'upload_url' | 'submitted_at' | 'submitted_by'
  >
> & {
  rapport_id: string
  inspectie_type: InspectieType
}

/** Empty installateur gegevens (used to initialise the digital form) */
export const emptyInstallateurGegevens = (): InstallateurGegevens => ({
  Bedrijfsnaam: '',
  NaamVerantwoordelijke: '',
  Functie: '',
  Certificatiestatus: '',
  Adres: '',
  PostcodeenPlaats: '',
  Emailadres: '',
  Telnr: '',
})
